<template>
    <Transition
        name="ds-modal"
        @after-enter="a11yOnAfterEnter"
        @leave="a11yOnLeave"
    >
        <div
            v-show="modalStore.open"
            :id="backdropId"
            :class="`ds-modal-bg ds-modal-bg-${kebabCase(modalStore.name)}`"
            role="dialog"
            :aria-modal="modalStore.open"
            :aria-hidden="!modalStore.open"
            :aria-label="modalStore.name"
            tabindex="-1"
            @click="onBackdropClick"
        >
            <div
                :id="modalStore.modalTarget.substring(1)"
                ref="modalTargetElement"
                class="ds-modal"
            />
        </div>
    </Transition>
</template>
<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, useNuxtApp } from '#imports';
import { lock, unlock, clearBodyLocks } from 'tua-body-scroll-lock';
import { useModalStore } from '~dsc/stores/modal';
import { kebabCase } from 'lodash-es';

const { $dsc } = useNuxtApp();
const modalStore = useModalStore();
const modalTargetElement = ref(null);
const previouslyFocusedElement = ref(null);

watch([() => modalStore.open, modalTargetElement], async () => {
    if (!modalTargetElement.value) return;

    if (modalStore.open) await nextTick();

    const [ target = modalTargetElement.value ] = modalTargetElement.value.getElementsByClassName('body');

    if (modalStore.open) {
        await $dsc.windowScrollTo();
        lock(target);
    } else {
        unlock(target);
    }
}, { immediate: true });

onUnmounted(clearBodyLocks);

const backdropId = 'ds-modal-backdrop';
function onBackdropClick(event) {
    if (modalStore.closeOnBackdropClick && event.target.id === backdropId) {
        modalStore.unsetNameAndClose();
    }
}

const FOCUSABLE_ELEMENTS_SELECTOR = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';

function onWindowKeyUp(e) {
    if (modalStore.open && (e.key === 'Escape' || e.key === 'Esc')) {
        modalStore.unsetNameAndClose();
    }
}

function onWindowKeyDown(e) {
    if (modalStore.open && e.key === 'Tab' && modalTargetElement.value) {
        const focusableElements = modalTargetElement.value.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR);
        const [firstFocusableElement] = focusableElements;
        const lastFocusableElement = focusableElements[focusableElements.length - 1];

        if (!firstFocusableElement) return;

        const focusIsNotInsideModal = document.activeElement === null || !modalTargetElement.value.contains(document.activeElement);

        if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement || focusIsNotInsideModal) {
                lastFocusableElement.focus();
                e.preventDefault();
            }
        } else if (document.activeElement === lastFocusableElement || focusIsNotInsideModal) {
            firstFocusableElement.focus();
            e.preventDefault();
        }
    }
}

onMounted(() => {
    window.addEventListener('keyup', onWindowKeyUp);
    window.addEventListener('keydown', onWindowKeyDown);
});

onUnmounted(() => {
    window.removeEventListener('keyup', onWindowKeyUp);
    window.removeEventListener('keydown', onWindowKeyDown);
});

const a11yOnAfterEnter = modalElement => {
    previouslyFocusedElement.value = document.activeElement;
    modalElement.focus();
};

const a11yOnLeave = () => {
    previouslyFocusedElement.value.focus();
};
</script>

<style scoped>
.ds-modal-enter-active,
.ds-modal-leave-active {
  visibility: visible;
  opacity: 1;
  transition: visibility 250ms ease-in-out, opacity 250ms;
}
.ds-modal-enter-active .ds-modal,
.ds-modal-leave-active .ds-modal {
  transform: translateY(0);
  transition: transform 250ms ease-in-out;
}

.ds-modal-enter-from,
.ds-modal-leave-to {
  visibility: hidden;
  opacity: 0;
  transition: visibility 250ms ease-in-out, opacity 250ms ease-in-out;
}
.ds-modal-enter-from .ds-modal,
.ds-modal-leave-to .ds-modal {
  transform: translateY(-100%);
  transition: transform 250ms ease-in-out;
}
</style>
